import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Swords, Trophy, Timer, Sparkles, TrendingUp, TrendingDown, Flame, Coins, Lock, Zap } from 'lucide-react';
import StreakLeaderboard from '@/components/StreakLeaderboard';
import WeeklyDigestCard from '@/components/WeeklyDigestCard';
import EliteWinsCard from '@/components/EliteWinsCard';
import SeasonCountdown from '@/components/SeasonCountdown';
import PrizePayoutHistory from '@/components/PrizePayoutHistory';
import SponsorshipsPanel from '@/components/SponsorshipsPanel';
import PayoutMilestoneBanner from '@/components/PayoutMilestoneBanner';
import WeeklyRoundupCard from '@/components/WeeklyRoundupCard';
import SponsorLoyaltyBadge from '@/components/SponsorLoyaltyBadge';

function BattleCoinCard({ coin, side, pct, votes, pool, isWinner, userChoice, onVote, disabled, stake, sponsoredBy }) {
  const up = (coin?.change_24h ?? 0) >= 0;
  const voted = userChoice === side;
  return (
    <div
      className={`relative overflow-hidden border p-6 flex flex-col ${voted ? 'border-[#FCD535] bg-[#FCD535]/8' : isWinner ? 'border-[#0ECB81] bg-[#0ECB81]/8' : 'border-[#2B3139] bg-[#181A20]'}`}
      data-testid={`battle-coin-${side}`}
    >
      {sponsoredBy && (
        <div className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-mono uppercase text-[#F6465D] bg-[#F6465D]/12 px-2 py-0.5 border border-[#F6465D]/40" data-testid={`sponsored-badge-${side}`}>
          SPONSORED · {sponsoredBy.name}
          {sponsoredBy.verified && (
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#0ECB81" strokeWidth="3" data-testid={`sponsor-verified-${side}`}>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
      )}
      {isWinner && (
        <div className="absolute top-2 right-2 flex items-center gap-1 text-[10px] font-mono uppercase text-[#0ECB81] bg-[#0ECB81]/15 px-2 py-0.5 border border-[#0ECB81]/40">
          <Trophy className="w-3 h-3" /> Winner
        </div>
      )}
      <div className="flex items-center gap-3 mb-3">
        {coin.image && <img src={coin.image} alt={coin.name} className="w-10 h-10" />}
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-lg truncate">{coin.name}</div>
          <div className="text-xs uppercase text-[#848E9C] font-mono">{coin.symbol}</div>
        </div>
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <div className="font-mono text-white text-xl">${coin.price?.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
        <div className={`font-mono text-xs flex items-center gap-0.5 ${up ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
          {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {up ? '+' : ''}{coin.change_24h?.toFixed(2)}%
        </div>
      </div>
      <div className="mt-auto">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-[#848E9C]">{votes} votes</span>
          <span className={`font-mono ${voted ? 'text-[#FCD535]' : 'text-[#EAECEF]'}`}>{pct}%</span>
        </div>
        <div className="w-full h-2 bg-[#2B3139] overflow-hidden">
          <div
            className={`h-full transition-all ${voted ? 'bg-[#FCD535]' : isWinner ? 'bg-[#0ECB81]' : 'bg-[#5E6673]'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-[10px] text-[#5E6673] mt-1.5 font-mono">
          <span>Pool: <span className="text-[#FCD535]">{(pool || 0).toFixed(0)} USDT</span></span>
          {voted && stake > 0 && <span className="text-[#0ECB81]">Your stake: {stake.toFixed(0)}</span>}
        </div>
        <Button
          onClick={onVote}
          disabled={disabled}
          size="sm"
          className={`w-full mt-4 rounded-sm font-semibold ${voted ? 'bg-[#FCD535] text-[#0B0E11]' : 'bg-[#2B3139] hover:bg-[#FCD535] hover:text-[#0B0E11] text-white'}`}
          data-testid={`battle-vote-${side}`}
        >
          {voted ? '✓ You voted' : `Vote ${coin.symbol}`}
        </Button>
      </div>
    </div>
  );
}

export default function Battles() {
  const { user } = useAuth();
  const [tier, setTier] = useState('standard');
  const [battle, setBattle] = useState(null);
  const [mult, setMult] = useState({ active: false, value: 1.0, base_value: 1.25, streak_value: 1.4, streak_threshold: 3, user_streak: 0, boosted: false, bonus_days: 7 });
  const [stakeBounds, setStakeBounds] = useState({ min: 25, max: 100 });
  const [eligible, setEligible] = useState(true);
  const [eliteMeta, setEliteMeta] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stakeInput, setStakeInput] = useState(0);
  const [pendingSide, setPendingSide] = useState(null); // 'a' | 'b' | null

  const load = async (currentTier) => {
    const t = currentTier || tier;
    const [b, h] = await Promise.all([
      api.get(`/battles/current?tier=${t}`),
      api.get('/battles/history?limit=10'),
    ]);
    setBattle(b.data.battle);
    setMult(b.data.multiplier || mult);
    setStakeBounds(b.data.stake_bounds || stakeBounds);
    setEligible(b.data.eligible !== false);
    setEliteMeta(b.data.elite_meta || null);
    setHistory(h.data.battles || []);
    setLoading(false);
  };

  useEffect(() => { load(tier); /* eslint-disable-next-line */ }, [tier]);

  const switchTier = (t) => {
    if (t === tier) return;
    setTier(t);
    setPendingSide(null);
    setStakeInput(0);
  };

  const submitVote = async (side) => {
    if (!user) return toast.error('Log in to vote');
    if (battle?.user_choice) return;
    if (tier === 'elite' && !eligible) return toast.error('Elite battles require a boosted streak');
    if (tier === 'elite' && stakeInput <= 0) return toast.error(`Elite votes require a ${stakeBounds.min}-${stakeBounds.max} USDT stake`);
    try {
      await api.post(`/battles/${battle.battle_id}/vote`, { choice: side, stake: stakeInput });
      toast.success(
        stakeInput > 0
          ? `Voted for ${side === 'a' ? battle.coin_a.symbol : battle.coin_b.symbol} with ${stakeInput} USDT staked`
          : `Voted for ${side === 'a' ? battle.coin_a.symbol : battle.coin_b.symbol}. Good luck!`
      );
      setPendingSide(null);
      setStakeInput(0);
      load(tier);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vote failed');
    }
  };

  const openVoteDialog = (side) => {
    setPendingSide(side);
    setStakeInput(tier === 'elite' ? stakeBounds.min : 0);
  };

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-10 text-[#848E9C]">Loading battle…</div>;

  const activeMultPct = Math.round(((mult.value || 1) - 1) * 100);
  const totalPool = (battle?.pool_a || 0) + (battle?.pool_b || 0);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
        <div>
          <div className="flex items-center gap-2">
            <Swords className="w-6 h-6 text-[#FCD535]" />
            <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Coin Battles</h1>
          </div>
          <p className="text-[#848E9C] text-sm mt-2 max-w-2xl">
            Two coins enter, one comes out on top. Vote for the coin you think the community will pump this week.
            Optionally stake <span className="text-[#FCD535]">{stakeBounds.min}-{stakeBounds.max} USDT</span> — winners split the losing pool.
            Correct picks {mult.streak_threshold}+ in a row boost your referral multiplier to <span className="text-[#FCD535]">+{Math.round((mult.streak_value - 1) * 100)}%</span>.
          </p>
        </div>
        {battle && (
          <div className="flex items-center gap-2 text-[#B7BDC6] text-sm bg-[#181A20] border border-[#2B3139] px-3 py-2 rounded-sm">
            <Timer className="w-4 h-4 text-[#FCD535]" />
            <span className="font-mono text-white">{battle.hours_remaining}h</span> remaining
          </div>
        )}
      </div>

      {/* Tier Tabs */}
      <div className="mt-5 inline-flex border border-[#2B3139] p-0.5 rounded-sm bg-[#181A20]" data-testid="tier-tabs">
        <button onClick={() => switchTier('standard')} data-testid="tier-tab-standard"
          className={`px-4 py-2 text-sm font-semibold rounded-sm flex items-center gap-2 ${tier === 'standard' ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}>
          <Swords className="w-3.5 h-3.5" /> Standard <span className="font-mono text-[10px] opacity-70">25-100</span>
        </button>
        <button onClick={() => switchTier('elite')} data-testid="tier-tab-elite"
          className={`px-4 py-2 text-sm font-semibold rounded-sm flex items-center gap-2 ${tier === 'elite' ? 'bg-[#F6465D] text-white' : 'text-[#B7BDC6] hover:text-white'}`}>
          <Zap className="w-3.5 h-3.5" /> Elite <span className="font-mono text-[10px] opacity-70">100-500</span>
        </button>
      </div>
      {tier === 'elite' && (
        <div className="mt-2 text-xs text-[#B7BDC6] flex items-center gap-2">
          <Flame className="w-3.5 h-3.5 text-[#F6465D]" />
          High-stakes weekly battle. Only accessible with an active +40% streak. Pool payouts <span className="text-[#F6465D] font-mono">×{eliteMeta?.pool_bonus || 1.5}</span>.
        </div>
      )}

      {user && (mult.user_streak > 0 || mult.active) && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {mult.active && (
            <div className={`flex items-center gap-2 border px-4 py-2.5 text-sm ${mult.boosted ? 'border-[#F6465D]/40 bg-[#F6465D]/10 text-[#F6465D]' : 'border-[#0ECB81]/40 bg-[#0ECB81]/10 text-[#0ECB81]'}`} data-testid="battle-mult-active">
              {mult.boosted ? <Flame className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              <span>
                {mult.boosted ? 'BOOSTED' : 'Active'} multiplier: <span className="font-mono">{mult.value.toFixed(2)}×</span> ({activeMultPct}% extra on referrals)
              </span>
            </div>
          )}
          {mult.user_streak > 0 && (
            <div className="flex items-center gap-2 border border-[#FCD535]/40 bg-[#FCD535]/8 px-4 py-2.5 text-[#FCD535] text-sm" data-testid="battle-streak">
              <Flame className="w-4 h-4" />
              <span>
                Prediction streak: <span className="font-mono">{mult.user_streak}</span> in a row
                {mult.user_streak >= mult.streak_threshold
                  ? ' — you are boosted!'
                  : ` — ${mult.streak_threshold - mult.user_streak} more to boost to +${Math.round((mult.streak_value - 1) * 100)}%`}
              </span>
            </div>
          )}
        </div>
      )}

      {!battle ? (
        <div className="mt-10 border border-[#2B3139] bg-[#181A20] p-8 text-center text-[#848E9C]">
          No active battle right now. Check back soon.
        </div>
      ) : tier === 'elite' && !eligible ? (
        <div className="mt-10 border border-[#F6465D]/40 bg-[#F6465D]/8 p-8 flex flex-col items-center text-center" data-testid="elite-locked">
          <div className="w-12 h-12 rounded-full bg-[#F6465D]/15 border border-[#F6465D]/40 grid place-items-center mb-3">
            <Lock className="w-5 h-5 text-[#F6465D]" />
          </div>
          <div className="text-white text-lg font-semibold mb-1">Elite Battles are locked</div>
          <div className="text-sm text-[#B7BDC6] max-w-md">
            You need an active <span className="text-[#FCD535] font-mono">+40%</span> boosted multiplier
            (achieved by getting <span className="text-[#FCD535]">{mult.streak_threshold}+ correct standard picks</span> in a row)
            to unlock elite high-stakes battles. Sneak preview shown below.
          </div>
          <button onClick={() => switchTier('standard')} data-testid="switch-to-standard-btn"
            className="mt-4 text-sm font-semibold text-[#FCD535] hover:text-[#F0B90B] underline underline-offset-4">
            ← Back to Standard
          </button>
          <div className="mt-6 w-full grid grid-cols-1 md:grid-cols-2 gap-3 opacity-50 pointer-events-none">
            <BattleCoinCard coin={battle.coin_a} side="a" pct={battle.pct_a} votes={battle.votes_a} pool={battle.pool_a}
              userChoice={null} stake={0} onVote={() => {}} disabled isWinner={false} />
            <BattleCoinCard coin={battle.coin_b} side="b" pct={battle.pct_b} votes={battle.votes_b} pool={battle.pool_b}
              userChoice={null} stake={0} onVote={() => {}} disabled isWinner={false} />
          </div>
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 relative">
            <BattleCoinCard
              coin={battle.coin_a} side="a"
              pct={battle.pct_a} votes={battle.votes_a} pool={battle.pool_a}
              userChoice={battle.user_choice}
              stake={battle.user_stake}
              onVote={() => openVoteDialog('a')}
              disabled={!!battle.user_choice}
              isWinner={false}
            />
            <div className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none">
              <div className="w-14 h-14 rounded-full border border-[#FCD535]/40 bg-[#0B0E11] grid place-items-center">
                <span className="font-display font-bold text-[#FCD535]">VS</span>
              </div>
            </div>
            <BattleCoinCard
              coin={battle.coin_b} side="b"
              pct={battle.pct_b} votes={battle.votes_b} pool={battle.pool_b}
              userChoice={battle.user_choice}
              stake={battle.user_stake}
              onVote={() => openVoteDialog('b')}
              disabled={!!battle.user_choice}
              isWinner={false}
            />
          </div>

          {/* Stake / Vote confirm inline panel */}
          {pendingSide && !battle.user_choice && (
            <div className="mt-4 border border-[#FCD535]/40 bg-[#FCD535]/8 p-4" data-testid="battle-stake-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-white">
                  <Coins className="w-4 h-4 text-[#FCD535]" />
                  <span className="font-semibold">
                    Confirm vote for {pendingSide === 'a' ? battle.coin_a.symbol : battle.coin_b.symbol}
                  </span>
                </div>
                <button onClick={() => setPendingSide(null)} className="text-[#848E9C] hover:text-white text-sm" data-testid="cancel-vote-btn">Cancel</button>
              </div>
              <div className="text-xs text-[#B7BDC6] mb-2">
                {tier === 'elite'
                  ? <>Elite stake required: <span className="text-[#F6465D] font-semibold">{stakeBounds.min}-{stakeBounds.max} USDT</span>. Winners get their stake back + <span className="text-[#F6465D] font-semibold">1.5×</span> share of losing pool.</>
                  : <>Optional: stake {stakeBounds.min}-{stakeBounds.max} USDT. Winners get their stake back plus a share of the losing pool.</>}
              </div>
              <div className="flex gap-2 mb-4 flex-wrap">
                {(tier === 'elite' ? [100, 250, 500] : [0, 25, 50, 100]).map(v => (
                  <button key={v} onClick={() => setStakeInput(v)} data-testid={`stake-preset-${v}`}
                    className={`flex-1 min-w-[70px] py-2 text-xs font-mono border rounded-sm ${stakeInput === v ? (tier === 'elite' ? 'border-[#F6465D] bg-[#F6465D] text-white font-semibold' : 'border-[#FCD535] bg-[#FCD535] text-[#0B0E11] font-semibold') : 'border-[#2B3139] bg-[#0B0E11] text-[#B7BDC6] hover:text-white'}`}>
                    {v === 0 ? 'No stake' : `${v} USDT`}
                  </button>
                ))}
              </div>
              <Button onClick={() => submitVote(pendingSide)}
                className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm"
                data-testid="confirm-vote-btn">
                {stakeInput > 0 ? `Vote & Stake ${stakeInput} USDT` : `Cast vote (no stake)`}
              </Button>
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="border border-[#2B3139] bg-[#181A20] p-4">
              <div className="text-xs uppercase text-[#848E9C]">Total votes</div>
              <div className="font-mono text-white text-2xl mt-1">{battle.total_votes}</div>
            </div>
            <div className="border border-[#2B3139] bg-[#181A20] p-4">
              <div className="text-xs uppercase text-[#848E9C]">Total pool</div>
              <div className="font-mono text-[#FCD535] text-2xl mt-1">{totalPool.toFixed(0)}</div>
              <div className="text-[10px] text-[#5E6673]">USDT staked</div>
            </div>
            <div className="border border-[#2B3139] bg-[#181A20] p-4">
              <div className="text-xs uppercase text-[#848E9C]">Winner mult</div>
              <div className="font-mono text-[#FCD535] text-2xl mt-1">+25%</div>
              <div className="text-[10px] text-[#5E6673]">boost to +40% on streak</div>
            </div>
            <div className="border border-[#2B3139] bg-[#181A20] p-4">
              <div className="text-xs uppercase text-[#848E9C]">Resets</div>
              <div className="font-mono text-white text-lg mt-2">Mon 00:00 UTC</div>
            </div>
          </div>
        </>
      )}

      {history.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-[#FCD535]" />
            <h2 className="text-white text-xl font-semibold">Past Battles</h2>
          </div>
          <div className="border border-[#2B3139] bg-[#181A20]" data-testid="battle-history">
            <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
              <div className="col-span-3">Week</div>
              <div className="col-span-3">Matchup</div>
              <div className="col-span-3">Winner</div>
              <div className="col-span-1 text-right">Votes</div>
              <div className="col-span-2 text-right">Pool payout</div>
            </div>
            {history.map(b => (
              <div key={b.battle_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm">
                <div className="col-span-3 text-[#B7BDC6] text-xs">{b.week_start?.slice(0, 10)}</div>
                <div className="col-span-3 flex items-center gap-1.5 text-white text-xs">
                  <span className="uppercase font-mono">{b.coin_a?.symbol}</span>
                  <span className="text-[#5E6673]">vs</span>
                  <span className="uppercase font-mono">{b.coin_b?.symbol}</span>
                </div>
                <div className="col-span-3 flex items-center gap-2">
                  {b.winning_coin?.image && <img src={b.winning_coin.image} alt="" className="w-5 h-5" />}
                  <span className="text-[#0ECB81] font-mono uppercase text-xs">{b.winning_coin?.symbol}</span>
                </div>
                <div className="col-span-1 text-right font-mono text-[#EAECEF] text-xs">
                  {(b.votes_a || 0) + (b.votes_b || 0)}
                </div>
                <div className="col-span-2 text-right font-mono text-[#FCD535] text-xs">
                  +{(b.loser_pool || 0).toFixed(0)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Digest widget (auth-only) */}
      {user && <WeeklyDigestCard />}

      {/* Elite Wins (only shows if user has any) */}
      {user && <EliteWinsCard />}

      {/* Sponsored Battle Slots */}
      {user && <SponsorshipsPanel />}

      {/* Sponsor Loyalty Badge (shows +2% multiplier if earned) */}
      {user && <SponsorLoyaltyBadge />}

      {/* Weekly Sponsor Roundup (public — social proof) */}
      <WeeklyRoundupCard />

      {/* Season Countdown */}
      <SeasonCountdown />

      {/* Prize Payout History */}
      <PrizePayoutHistory />
      <PayoutMilestoneBanner />

      {/* Streak Leaderboard */}
      <StreakLeaderboard />
    </div>
  );
}
